import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import { Plus, Edit2, Trash2, Image as ImageIcon, X, Upload, ShoppingBag, Search, TrendingUp, Percent, ChevronUp, ChevronDown, Minus, Infinity } from 'lucide-react';
import { Product } from '../../types';

interface AdminProductsProps {
  db: any;
  getCollectionPath: (col: string) => string;
  userId?: string;
}

export function AdminProducts({ db, getCollectionPath, userId }: AdminProductsProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Bulk update states
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkPercent, setBulkPercent] = useState<number | ''>('');
  const [bulkType, setBulkType] = useState<'increase' | 'decrease'>('increase');
  const [bulkRound, setBulkRound] = useState<number>(1000);
  const [bulkDirection, setBulkDirection] = useState<'up' | 'down' | 'nearest'>('up');
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  // Form states
  const [name, setName] = useState('');
  const [price, setPrice] = useState<number | ''>('');
  const [stock, setStock] = useState<number | ''>('');
  const [trackStock, setTrackStock] = useState<boolean>(true);
  const [minStockAlert, setMinStockAlert] = useState<number | ''>('');
  const [imageUrl, setImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasSeeded = useRef(false);

  useEffect(() => {
    if (!userId) return;

    const productsRef = collection(db, getCollectionPath('products'));
    const q = query(productsRef, where('clubId', '==', userId));

    const seedDefaultProducts = async () => {
      const DEFAULT_PRODUCTS = [
        { name: "Cerveza Litro", price: 8000, imageUrl: "/products/Cerveza Litro.webp" },
        { name: "Cerveza Pinta", price: 5000, imageUrl: "/products/Cerveza Pinta.webp" },
        { name: "Agua Saborizada", price: 3000, imageUrl: "/products/Agua Saborizada.webp" },
        { name: "Tubo pelotas", price: 8500, imageUrl: "/products/Tubo pelotas.webp" },
        { name: "Gaseosa 500cc", price: 3500, imageUrl: "/products/Gaseosa 500cc.webp" },
        { name: "Pizza Muzza", price: 15000, imageUrl: "/products/Pizza muzza.webp" },
        { name: "Gatorade (isotónica)", price: 6000, imageUrl: "/products/Gatorade (isotónica).webp" },
        { name: "Agua", price: 3000, imageUrl: "/products/Agua.webp" },
        { name: "Cubre grip", price: 2500, imageUrl: "/products/Cubre grip.webp" }
      ];

      for (const prod of DEFAULT_PRODUCTS) {
        try {
          await addDoc(productsRef, {
            name: prod.name,
            price: prod.price,
            imageUrl: prod.imageUrl,
            clubId: userId,
            createdAt: Date.now()
          });
        } catch (err) {
          console.error("Error seeding default product:", err);
        }
      }
    };

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let prodList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Product[];

      if (prodList.length === 0 && !hasSeeded.current) {
        hasSeeded.current = true;
        seedDefaultProducts();
      }

      // Sort by creation date
      prodList.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setProducts(prodList);
    });

    return () => unsubscribe();
  }, [db, getCollectionPath, userId]);

  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const MAX_SIZE = 800;

          if (width > height) {
            if (width > MAX_SIZE) {
              height *= MAX_SIZE / width;
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width *= MAX_SIZE / height;
              height = MAX_SIZE;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.6));
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const compressedBase64 = await compressImage(file);
      setImageUrl(compressedBase64);
    } catch (error) {
      console.error('Error compressing image:', error);
      alert('Error al procesar la imagen');
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setPrice('');
    setStock('');
    setTrackStock(true);
    setMinStockAlert('');
    setImageUrl('');
    setEditingProduct(null);
  };

  const openModal = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setName(product.name);
      setPrice(product.price);
      setStock(product.stock !== undefined && product.stock !== null ? product.stock : '');
      setTrackStock(product.trackStock !== false);
      setMinStockAlert(product.minStockAlert !== undefined && product.minStockAlert !== null ? product.minStockAlert : '');
      setImageUrl(product.imageUrl || '');
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      alert('El nombre es obligatorio');
      return;
    }
    if (price === '' || isNaN(Number(price))) {
      alert('El precio debe ser un número válido');
      return;
    }

    try {
      let finalImageUrl = imageUrl;
      if (finalImageUrl.startsWith('data:image/')) {
        setIsUploading(true);
        const imageRef = ref(storage, `products/${userId || 'admin'}_${Date.now()}.jpg`);
        await uploadString(imageRef, finalImageUrl, 'data_url');
        finalImageUrl = await getDownloadURL(imageRef);
      }

      const productsRef = collection(db, getCollectionPath('products'));

      if (editingProduct) {
        const docRef = doc(productsRef, editingProduct.id);
        await updateDoc(docRef, {
          name: name.trim(),
          price: Number(price),
          trackStock: trackStock,
          stock: trackStock && stock !== '' ? Number(stock) : null,
          minStockAlert: trackStock && minStockAlert !== '' ? Number(minStockAlert) : null,
          imageUrl: finalImageUrl,
          updatedAt: Date.now()
        });
      } else {
        await addDoc(productsRef, {
          name: name.trim(),
          price: Number(price),
          trackStock: trackStock,
          stock: trackStock && stock !== '' ? Number(stock) : null,
          minStockAlert: trackStock && minStockAlert !== '' ? Number(minStockAlert) : null,
          imageUrl: finalImageUrl,
          clubId: userId || '',
          createdAt: Date.now()
        });
      }
      setIsUploading(false);
      closeModal();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error al guardar el producto');
      setIsUploading(false);
    }
  };

  const handleDelete = async (productId: string) => {
    if (!await window.confirm('¿Estás seguro de que deseas eliminar este producto?')) return;

    try {
      const docRef = doc(collection(db, getCollectionPath('products')), productId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error al eliminar el producto');
    }
  };

  const handleAskConfirm = () => {
    if (bulkPercent === '' || Number(bulkPercent) <= 0) {
      alert('Ingresa un porcentaje válido (mayor a 0)');
      return;
    }
    
    const count = products.length;
    if (count === 0) return;

    setShowBulkConfirm(true);
  };

  const handleBulkUpdate = async () => {
    setIsBulkUpdating(true);
    try {
      const batchPromises = products.map(p => {
        let newPrice = p.price;
        const factor = Number(bulkPercent) / 100;
        
        if (bulkType === 'increase') {
          newPrice = newPrice * (1 + factor);
        } else {
          newPrice = newPrice * (1 - factor);
        }

        // Rounding
        if (bulkRound > 0) {
          if (bulkDirection === 'up') {
            newPrice = Math.ceil(newPrice / bulkRound) * bulkRound;
          } else if (bulkDirection === 'down') {
            newPrice = Math.floor(newPrice / bulkRound) * bulkRound;
          } else {
            newPrice = Math.round(newPrice / bulkRound) * bulkRound;
          }
        } else {
          newPrice = Math.round(newPrice);
        }

        if (newPrice !== p.price) {
          const docRef = doc(collection(db, getCollectionPath('products')), p.id);
          return updateDoc(docRef, { price: newPrice, updatedAt: Date.now() });
        }
        return Promise.resolve();
      });

      await Promise.all(batchPromises);
      setIsBulkModalOpen(false);
      setShowBulkConfirm(false);
      setBulkPercent('');
    } catch (err) {
      console.error('Error in bulk update:', err);
      alert('Error al actualizar precios masivamente');
    } finally {
      setIsBulkUpdating(false);
      setShowBulkConfirm(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 relative">
        <div className="w-full sm:w-auto sm:flex-1 flex justify-start">
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
            <input
              type="text"
              placeholder="Buscar producto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-black/20 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#FFD145]/50 focus:ring-1 focus:ring-[#FFD145]/50 transition-all text-sm"
            />
          </div>
        </div>
        <div className="text-center flex-1">
          <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-white">Mis Productos</h2>
          <p className="text-sm text-zinc-400 mt-1">Administra los productos y artículos del club.</p>
        </div>
        <div className="flex-1 flex justify-end w-full sm:w-auto gap-2">
          <button
            onClick={() => {
              setShowBulkConfirm(false);
              setIsBulkModalOpen(true);
            }}
            className="w-full sm:w-auto flex justify-center items-center gap-2 px-3 py-1.5 text-xs bg-[#2a2d3d] hover:bg-[#32364a] text-white font-black uppercase tracking-wider rounded-xl transition-all border border-white/10"
            title="Actualizador Masivo de Precios"
          >
            <Percent size={14} className="text-[#FFD145]" />
            <span className="">Actualizar Precios</span>
          </button>
          <button
            onClick={() => openModal()}
            className="w-full sm:w-auto flex justify-center items-center gap-2 px-3 py-1.5 text-xs bg-[#FFD145] hover:bg-[#e6b933] text-black font-black uppercase tracking-wider rounded-xl transition-all shadow-[0_0_15px_rgba(255,209,69,0.3)]"
          >
            <Plus size={16} />
            <span className="">Nuevo Producto</span>
          </button>
        </div>
      </div>

      {products.length === 0 ? (
        <div className="bg-black/20 rounded-2xl p-12 text-center">
          <ShoppingBag size={48} className="mx-auto text-zinc-600 mb-4" />
          <h3 className="text-lg font-bold text-white mb-2">No tienes productos</h3>
          <p className="text-zinc-400 max-w-sm mx-auto">
            Añade productos como pelotas, bebidas, grips o cualquier artículo que vendas en tu club.
          </p>
          <button
            onClick={() => openModal()}
            className="mt-6 px-6 py-2 bg-white/10 hover:bg-white/15 text-white rounded-xl font-medium transition-colors border border-white/10"
          >
            Añadir mi primer producto
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3">
          {products.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase())).map(product => (
            <div
              key={product.id}
              onClick={() => openModal(product)}
              className="bg-[#1a1c23] border border-[#2d313f] rounded-2xl overflow-hidden group hover:border-theme-primary/30 transition-all shadow-xl flex flex-col aspect-square cursor-pointer"
            >
              {/* Product Image Area */}
              <div className="flex-1 bg-black/40 relative flex items-center justify-center overflow-hidden">
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity group-hover:scale-105 duration-500"
                  />
                ) : (
                  <ImageIcon size={32} className="text-zinc-700" />
                )}

                {/* Actions overlay */}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <button
                    onClick={(e) => { e.stopPropagation(); openModal(product); }}
                    className="p-2 bg-black/60 hover:bg-sky-500/80 text-white rounded-lg backdrop-blur-sm transition-colors"
                    title="Editar producto"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(product.id); }}
                    className="p-2 bg-black/60 hover:bg-red-500/80 text-white rounded-lg backdrop-blur-sm transition-colors"
                    title="Eliminar producto"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                
                {/* Stock badge */}
                {(product.trackStock === false) ? (
                  <div className="absolute top-2 left-2 p-1 rounded-md shadow-md backdrop-blur-sm z-10 bg-black/60 text-zinc-300" title="Stock infinito">
                    <Infinity size={22} />
                  </div>
                ) : (product.stock !== undefined && product.stock !== null) ? (
                  <div className={`absolute top-2 left-2 px-2 py-1 rounded-md text-[10px] font-black tracking-widest uppercase shadow-md backdrop-blur-sm z-10 ${
                    product.stock <= (product.minStockAlert || 0) ? 'bg-red-500/90 text-white' : 'bg-black/60 text-emerald-400'
                  }`}>
                    Stock: {product.stock}
                  </div>
                ) : null}
              </div>

              {/* Product Info */}
              <div className="p-2 flex flex-col items-center justify-center text-center h-[56px] sm:h-[64px]">
                <h3 className="font-bold text-white/90 line-clamp-2 text-[10px] sm:text-xs leading-tight h-[28px] flex items-center" title={product.name}>
                  {product.name}
                </h3>
                <div className="mt-0.5">
                  <span className="text-sm sm:text-base font-black text-white">
                    ${product.price}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-[#242735] border border-white/10 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="p-4 border-b border-white/5 flex justify-between items-center bg-[#2a2d3d]">
              <h3 className="font-bold text-lg text-white">
                {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
              </h3>
              <button
                onClick={closeModal}
                className="p-1 text-zinc-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">
                  Nombre del Producto
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: Tubo de Pelotas Head Pro"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-theme-primary/50 focus:ring-1 focus:ring-theme-primary/50 transition-all"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-black/30 border border-white/10 rounded-xl">
                <div>
                  <label className="block text-sm font-bold text-white mb-0.5">Controlar Stock</label>
                  <p className="text-xs text-zinc-400">Si está desactivado, el stock será infinito.</p>
                </div>
                <button
                  onClick={() => setTrackStock(!trackStock)}
                  className={`w-12 h-6 rounded-full transition-colors relative ${trackStock ? 'bg-[#FFD145]' : 'bg-zinc-600'}`}
                >
                  <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-transform ${trackStock ? 'left-7' : 'left-1'}`} />
                </button>
              </div>

              {trackStock && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">
                        Precio ($)
                      </label>
                      <input
                        type="number"
                        value={price}
                        onChange={(e) => setPrice(e.target.value ? Number(e.target.value) : '')}
                        placeholder="Ej: 8500"
                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-theme-primary/50 focus:ring-1 focus:ring-theme-primary/50 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">
                        Stock Actual
                      </label>
                      <input
                        type="number"
                        value={stock}
                        onChange={(e) => setStock(e.target.value !== '' ? Number(e.target.value) : '')}
                        placeholder="Ej: 10"
                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-theme-primary/50 focus:ring-1 focus:ring-theme-primary/50 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">
                      Aviso límite de stock
                    </label>
                    <input
                      type="number"
                      value={minStockAlert}
                      onChange={(e) => setMinStockAlert(e.target.value !== '' ? Number(e.target.value) : '')}
                      placeholder="Ej: Avisar cuando queden 5"
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-theme-primary/50 focus:ring-1 focus:ring-theme-primary/50 transition-all"
                    />
                  </div>
                </>
              )}

              {!trackStock && (
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">
                      Precio ($)
                    </label>
                    <input
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value ? Number(e.target.value) : '')}
                      placeholder="Ej: 8500"
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-theme-primary/50 focus:ring-1 focus:ring-theme-primary/50 transition-all"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">
                  Foto (Opcional)
                </label>
                <div
                  className={`w-full h-32 border-2 border-dashed ${imageUrl ? 'border-theme-primary/30' : 'border-white/10'} rounded-xl flex items-center justify-center relative overflow-hidden bg-black/20 group cursor-pointer`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {imageUrl ? (
                    <>
                      <img src={imageUrl} alt="Preview" className="w-full h-full object-contain p-2" />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center backdrop-blur-sm">
                        <Edit2 size={24} className="text-white mb-2" />
                        <span className="text-sm font-medium text-white">Cambiar foto</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center text-zinc-500 group-hover:text-theme-primary transition-colors">
                      {isUploading ? (
                        <span className="animate-pulse">Procesando...</span>
                      ) : (
                        <>
                          <Upload size={28} className="mb-2" />
                          <span className="text-sm font-medium">Subir imagen</span>
                        </>
                      )}
                    </div>
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-white/5 bg-[#2a2d3d] flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2.5 rounded-xl font-medium text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={isUploading}
                className="px-6 py-2.5 rounded-xl font-black bg-[#FFD145] hover:bg-[#e6b933] text-black uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(255,209,69,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
      {/* Bulk Update Modal */}
      {isBulkModalOpen && createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-[#242735] border border-white/10 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="p-4 border-b border-white/5 flex justify-between items-center bg-[#2a2d3d]">
              <div className="flex items-center gap-2">
                <TrendingUp size={20} className="text-[#FFD145]" />
                <h3 className="font-bold text-lg text-white">Actualizador de Precios</h3>
              </div>
              <button
                onClick={() => setIsBulkModalOpen(false)}
                className="p-1 text-zinc-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-6">
              {/* Type & Percent */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">
                    Acción
                  </label>
                  <div className="flex bg-black/40 rounded-xl border border-white/10 p-1">
                    <button
                      onClick={() => setBulkType('increase')}
                      className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-sm font-bold transition-all ${bulkType === 'increase' ? 'bg-[#FFD145] text-black' : 'text-zinc-400 hover:text-white'}`}
                    >
                      <Plus size={14} /> Aumentar
                    </button>
                    <button
                      onClick={() => setBulkType('decrease')}
                      className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-sm font-bold transition-all ${bulkType === 'decrease' ? 'bg-red-500 text-white' : 'text-zinc-400 hover:text-white'}`}
                    >
                      <Minus size={14} /> Bajar
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">
                    Porcentaje (%)
                  </label>
                  <div className="relative">
                    <Percent size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                      type="number"
                      value={bulkPercent}
                      onChange={(e) => setBulkPercent(e.target.value ? Number(e.target.value) : '')}
                      placeholder="Ej: 15"
                      className="w-full bg-black/40 border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-[#FFD145]/50 focus:ring-1 focus:ring-[#FFD145]/50 transition-all font-bold"
                    />
                  </div>
                </div>
              </div>

              {/* Rounding Settings */}
              <div className="space-y-4 pt-4 border-t border-white/5">
                <div>
                  <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">
                    Redondear cada ($)
                  </label>
                  <input
                    type="number"
                    value={bulkRound}
                    onChange={(e) => setBulkRound(Number(e.target.value) || 0)}
                    placeholder="Ej: 500"
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-theme-primary/50 transition-all"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Los precios se ajustarán al múltiplo más cercano de este valor.</p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">
                    Regla de redondeo
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setBulkDirection('up')}
                      className={`flex flex-col items-center justify-center gap-1 p-2 rounded-xl border transition-all ${bulkDirection === 'up' ? 'bg-white/10 border-white/20 text-white' : 'bg-black/20 border-transparent text-zinc-500 hover:bg-white/5'}`}
                    >
                      <ChevronUp size={16} className={bulkDirection === 'up' ? 'text-[#FFD145]' : ''} />
                      <span className="text-xs font-bold">Hacia Arriba</span>
                    </button>
                    <button
                      onClick={() => setBulkDirection('nearest')}
                      className={`flex flex-col items-center justify-center gap-1 p-2 rounded-xl border transition-all ${bulkDirection === 'nearest' ? 'bg-white/10 border-white/20 text-white' : 'bg-black/20 border-transparent text-zinc-500 hover:bg-white/5'}`}
                    >
                      <div className="w-3 h-3 rounded-full bg-current mb-0.5" />
                      <span className="text-xs font-bold">Al Cercano</span>
                    </button>
                    <button
                      onClick={() => setBulkDirection('down')}
                      className={`flex flex-col items-center justify-center gap-1 p-2 rounded-xl border transition-all ${bulkDirection === 'down' ? 'bg-white/10 border-white/20 text-white' : 'bg-black/20 border-transparent text-zinc-500 hover:bg-white/5'}`}
                    >
                      <ChevronDown size={16} className={bulkDirection === 'down' ? 'text-red-400' : ''} />
                      <span className="text-xs font-bold">Hacia Abajo</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {showBulkConfirm ? (
              <div className="p-4 border-t border-white/5 bg-[#2a2d3d] flex flex-col gap-3">
                <p className="text-sm text-yellow-500 font-medium text-center">
                  ¿Estás seguro? Se actualizarán los precios de {products.length} productos. Esta acción no se puede deshacer.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowBulkConfirm(false)}
                    className="px-4 py-2.5 rounded-xl font-medium text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
                  >
                    Volver atrás
                  </button>
                  <button
                    onClick={handleBulkUpdate}
                    disabled={isBulkUpdating}
                    className="px-6 py-2.5 rounded-xl font-black bg-[#FFD145] hover:bg-[#e6b933] text-black uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(255,209,69,0.3)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isBulkUpdating ? (
                      <span className="animate-pulse">Actualizando...</span>
                    ) : (
                      <>Sí, confirmar cambios</>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-4 border-t border-white/5 bg-[#2a2d3d] flex justify-end gap-3">
                <button
                  onClick={() => setIsBulkModalOpen(false)}
                  className="px-4 py-2.5 rounded-xl font-medium text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAskConfirm}
                  disabled={!bulkPercent || products.length === 0}
                  className="px-6 py-2.5 rounded-xl font-black bg-[#FFD145] hover:bg-[#e6b933] text-black uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(255,209,69,0.3)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  Aplicar a {products.length} productos
                </button>
              </div>
            )}
          </div>
        </div>
      , document.body)}
    </div>
  );
}
